import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

export type MenuItem = { key: string; label: string; onSelect: () => void };

/**
 * An icon button that drops a list of actions below it. Focus moves into the
 * list on open; arrows walk it, Escape closes it back onto the button, and a
 * pick or a press outside closes it too.
 */
export function Menu({
  label,
  triggerLabel,
  title,
  icon,
  items,
}: {
  /** Names the list (`aria-label`). */
  label: string;
  /** Names the button (`aria-label`); `title` is its hover tooltip. */
  triggerLabel: string;
  title?: string;
  icon: ReactNode;
  items: MenuItem[];
}) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    buttons.current[0]?.focus();
    const onPointerDown = (event: globalThis.PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function onKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    const at = buttons.current.indexOf(
      document.activeElement as HTMLButtonElement,
    );
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      button.current?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = (at + step + items.length) % items.length;
      buttons.current[next]?.focus();
    }
  }

  return (
    <div className="menu-anchor" ref={root}>
      <button
        ref={button}
        type="button"
        className={open ? 'btn-icon-ghost is-active' : 'btn-icon-ghost'}
        aria-label={triggerLabel}
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        {icon}
      </button>
      {open && (
        <ul
          id={menuId}
          role="menu"
          aria-label={label}
          className="popover menu-list"
          onKeyDown={onKeyDown}
        >
          {items.map((item, index) => (
            <li key={item.key} role="none">
              <button
                ref={(el) => {
                  buttons.current[index] = el;
                }}
                type="button"
                role="menuitem"
                className="menu-item"
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
